import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { GenericDataParameters } from "../Tables/_Tables/useColumnConstructor";
import { Table } from "@tanstack/react-table";
import { colors, fonts, glyphs } from "../../Styles";
import { Button } from "..";
import { ButtonTypes, SelectFieldOption_Values } from "../../Definitions";
import { createPopper } from "@popperjs/core";
import { TestData as AutoReports } from "../../Assets/TestData/autoReports";
import { Params, ValueType } from "../../Definitions/valueTypes";

type filterPopupProps<T> = {
  accessorData: GenericDataParameters<T>[];
  table: Table<T>;
  autoReport?: number;
};

export default function FilterPopup<T>({ accessorData, table, autoReport, }: filterPopupProps<T>) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState<Record<string, boolean>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, any[]>>({});

  // #region | Create Popper
  const [showTooltip, setShowTooltip] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (buttonRef.current && tooltipRef.current) {
      createPopper(buttonRef.current, tooltipRef.current, {
        placement: "bottom",
        modifiers: [
          {
            name: "arrow",
            options: {
              padding: 0,
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 5],
            },
          },
        ],
      });
    }
  }, []);

  // Close the popover when clicking outside or on the content div
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  // #endregion

  // Show / hide filters
  const toggleFilter = (accessor: string) => {
    setSelectedFilters((prevState) => {
      const newFilters = { ...prevState };
      if (newFilters[accessor]) {
        delete newFilters[accessor];
      } else {
        newFilters[accessor] = [];
      }
      return newFilters;
    });
  };

  // Reset all filters and close all filter sections
  const resetFilters = () => {
    accessorData
      .filter((column) => column.filter === true)
      .forEach((column) => {
        table.getColumn(column.accessor)?.setFilterValue("");
      });

    setSelectedFilters({});
  };

  useEffect(() => {
    if (autoReport) {
      const selectedReport = AutoReports.find((a) => a.reportId === autoReport);
  
      if (selectedReport?.filterParameter) {
        const filters = selectedReport.filterParameter.map((p) => ({
          id: p.key,
          value: p.value,
        }));
  
        const filtersRecord = filters.reduce((acc, filter) => {
          acc[filter.id] = filter.value;
          return acc;
        }, {} as Partial<Record<keyof Params, ValueType[]>>);
  
        setSelectedFilters(filtersRecord);
  
        filters.forEach((filter) => {
          const col = table.getColumn(filter.id);
  
          if (col && Array.isArray(filter.value)) {
            const options = accessorData.find(
              (d) => d.accessor === filter.id
            )?.options;
  
            const optionsValues =
              typeof options === "function" ? options() : options;
  
            const allowedOptions = optionsValues?.filter(
              (opt) =>
                opt.id !== undefined &&
                filter.value.some((v) => v.id === opt.id)
            );
  
            col.setFilterValue(allowedOptions);
          }
        });
      }
    }
  }, [autoReport, table, accessorData]);
  

  return (
    <>
      <div ref={buttonRef} style={{ width: "fit-content" }}>
        <Button
          content={"Filter"}
          onClick={() => setShowTooltip(!showTooltip)}
          startEnhancer={glyphs.FilterParameter({})}
          buttonType={ButtonTypes.tableButton}
        />
      </div>
      <div
        ref={tooltipRef}
        style={{
          visibility: showTooltip ? "visible" : "hidden",
          opacity: showTooltip ? 1 : 0,
          backgroundColor: colors.greyDarkPrimary,
          borderRadius: "10px",
          padding: "10px",
          border: `5px solid ${colors.greyDarkPrimary}`,
          width: "180px",
          zIndex: 1000,
          position: "absolute",
        }}
      >
        <Button
          content={t("buttons.resetFilters", { defaultValue: "reset filters" })}
          onClick={resetFilters}
          buttonType={ButtonTypes.textButton}
        />
        <div style={{ width: "100%", height: "2px", backgroundColor: colors.greyDarkSecondary, margin: "10px 0" }} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            maxHeight: "200px",
            overflowY: "scroll",
            scrollbarWidth: "thin",
            scrollbarColor: `${colors.greyDarkSecondary} ${colors.transparent}`,
          }}
        >
          {accessorData
            .filter((column) => column.filter === true)
            .map((column) => {
              const columnOptions = column?.options;
              const options =  typeof columnOptions === "function" ? columnOptions() : columnOptions;

              return (
                <div key={column.accessor}>
                  <div
                    onClick={() => options && options.length > 0 ? toggleFilter(column.accessor) : undefined }
                    style={{ display: "flex", gap: "10px", cursor: "pointer" }}
                  >
                    <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyLightTertiary }} >
                      {column.headerValue}
                    </p>
                    <glyphs.Arrow
                      transparency={selectedFilters[column.accessor] ? 1 : 0.3}
                      rotate={selectedFilters[column.accessor] ? 90 : 0}
                      size="15px"
                    />
                  </div>
                  {selectedFilters[column.accessor] ? ( <>
                    <div style={{ display: "flex", flexDirection: "column", gap: "5px", padding: "10px" }} >
                      {options && options.map((option, index) => {
                        const hoverKey = `${column.accessor}-${option.id}`;
                        const currentFilter = (table.getColumn(column.accessor)?.getFilterValue() as SelectFieldOption_Values[]) || [];
                        const isSelected = currentFilter.some((val) => val.id === option.id);

                        return (
                          <div
                            key={option.id}
                            onClick={() => {
                              const col = table.getColumn(column.accessor);
                              const newFilter = currentFilter
                                ?.map((filterParameter) => filterParameter.id)
                                ?.includes(option.id as number)
                                ? currentFilter.filter( (filterParameter) => filterParameter.id !== option.id )
                                : [...(currentFilter || []), option];

                              col?.setFilterValue(newFilter.length > 0 ? newFilter : undefined);
                            }}

                            onMouseEnter={() => setIsHovered((prev) => ({ ...prev, [hoverKey]: true })) }
                            onMouseLeave={() => setIsHovered((prev) => ({ ...prev, [hoverKey]: false })) }
                            style={{
                              padding: "5px 10px",
                              borderRadius: "10px",
                              cursor: "pointer",
                              width: "fit-content",
                              backgroundColor:
                                isSelected || isHovered[hoverKey]
                                  ? colors.greyDarkSecondary
                                  : colors.transparent,
                              color: colors.greyLightTertiary,
                              ...fonts.paragraph,
                              ...fonts.fontSize4,
                            }}
                          >
                            {option.label}
                          </div>
                        );
                        
                      })}
                    </div>
                    <div style={{ height: "2px", width: "80%", backgroundColor: colors.greyDarkSecondary }} />
                  </> ) : ( <></> )}
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
}
